import { motion } from "motion/react";
import { useState } from "react";
import type { CheckpointResponse } from "../../lib/schemas";

interface CheckpointCardProps {
  onSubmit: (response: CheckpointResponse) => void;
  isSubmitting: boolean;
}

export function CheckpointCard({
  onSubmit,
  isSubmitting,
}: CheckpointCardProps) {
  const [feeling, setFeeling] = useState<"better" | "same" | "worse" | null>(
    null,
  );
  const [isHelpful, setIsHelpful] = useState<boolean | null>(null);

  const handleContinue = () => {
    if (feeling === null || isHelpful === null) return;
    onSubmit({
      feeling,
      isHelpful,
      wantsToContinue: true,
    });
  };

  const handleExit = () => {
    onSubmit({
      feeling: feeling ?? "same",
      isHelpful: isHelpful ?? false,
      wantsToContinue: false,
    });
  };

  const canSubmit = feeling !== null && isHelpful !== null && !isSubmitting;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="flex justify-start mb-4"
    >
      <div className="relative max-w-[90%] md:max-w-[75%] px-6 py-5 rounded-2xl rounded-bl-md bg-linear-to-br from-teal-50 to-emerald-50 border-2 border-emerald-200 shadow-lg shadow-emerald-900/5">
        {/* Header */}
        <div className="flex gap-2 items-center mb-4">
          <div className="bg-linear-to-r from-emerald-400 to-teal-500 h-2 w-2 rounded-full" />
          <span className="font-semibold text-emerald-700 text-xs tracking-wide uppercase">
            Check-in
          </span>
        </div>

        {/* Wellbeing Check */}
        <div className="mb-5">
          <p className="text-stone-700 text-[15px] mb-3">
            <b>How are you feeling right now</b> compared to when we started
            talking?
          </p>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: "better" as const, emoji: "😊", label: "Better" },
              { value: "same" as const, emoji: "😐", label: "About the same" },
              { value: "worse" as const, emoji: "😔", label: "Not great" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFeeling(option.value)}
                disabled={isSubmitting}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${
                    feeling === option.value
                      ? "bg-emerald-600 text-white shadow-md"
                      : "bg-white text-stone-600 border border-stone-200 hover:border-emerald-300 hover:bg-emerald-50"
                  }
                  ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}
                `}
              >
                <span className="mr-1.5">{option.emoji}</span>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Helpfulness Check */}
        <div className="mb-5">
          <p className="text-stone-700 text-[15px] mb-3">
            Has our conversation been <b>helpful</b> so far?
          </p>
          <div className="flex gap-2">
            {[
              { value: true, label: "Yes, it's helping" },
              { value: false, label: "Not really" },
            ].map((option) => (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => setIsHelpful(option.value)}
                disabled={isSubmitting}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${
                    isHelpful === option.value
                      ? "bg-emerald-600 text-white shadow-md"
                      : "bg-white text-stone-600 border border-stone-200 hover:border-emerald-300 hover:bg-emerald-50"
                  }
                  ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}
                `}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Continue/Exit Buttons */}
        <div className="flex gap-3 pt-2 border-t border-emerald-200">
          <button
            type="button"
            onClick={handleExit}
            disabled={isSubmitting}
            className={`
              flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
              bg-white text-stone-600 border border-stone-200 hover:bg-stone-50
              ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}
            `}
          >
            Take a break
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canSubmit}
            className={`
              flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
              ${
                canSubmit
                  ? "bg-linear-to-r from-emerald-600 to-teal-600 text-white shadow-md hover:shadow-lg"
                  : "bg-stone-100 text-stone-400 cursor-not-allowed"
              }
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <title>Loading</title>
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Submitting...
              </span>
            ) : (
              "Continue chatting"
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
